// ExploreFilters()
/*
   Usage:

   let ef = ExplorerFilters({site: '1/2/3'});
   ef.search = "My search text";
   ef.date_range = [new Date(), new Date()];
   if (ef.sort_desc == 'D')
     console.log("Sort is desc");
   query = ef.as_query()
   console.log("Filters query parameters:", query);
 */

function deep_copy(obj) {
  return JSON.parse(JSON.stringify(obj))
}

let _explorer_filters_id = 1

class ExplorerFilters {
  properties = {
    site: {
      component: 'ChipGeneric',
    },

    centro: {
      api_name: null, // Do not include in query
      component: 'ChipGeneric',
    },

    centro_list: {
      api_name: 'centros',
      component: 'ChipGeneric',
      serialize: (c) => c.join(','),
    },

    pk: {
      // Validate: "QUnit#NCDEFAULT#11#3ea88ead-3fa8-49af-aea4-58bddfb05559"
      validate: v => v === null || v.indexOf('QUnit#') == 0 || 'Invalid QUnit PK',
      component: 'ChipGeneric',
    },

    process_id: {
      api_name: 'affected_process',
      component: 'ChipProcess',
    },

    processes_ids: {
      api_name: 'affected_processes',
      // serialize: v => encodeURIComponent(v.map(p => p.id)), // process id
      serialize: encodeURIComponent, // process id
      component: 'ChipProcesses',
    },

    business_process: {
      api_name: 'business_process',
      component: 'ChipGeneric',
    },

    business_process_include: {
      api_name: 'business_process_include',
      component: 'ChipGeneric',
    },

    business_process_exclude: {
      api_name: 'business_process_exclude',
      component: 'ChipGeneric',
    },

    units_per_page: {
      api_name: 'items_per_page',
      // default: 10, // Fix bug where pagination would reset to 10
      component: 'ChipGeneric',
    },

    page: {
      api_name: 'offset',
      // default: 1, // Fix bug where current page would reset to 1
      serialize: (v, state) => (state.units_per_page || 10) * ((v || 1) - 1), // units offset
      // state.units_per_page * (v - 1), // units offset
      component: 'ChipGeneric',
    },

    search: {
      api_name: 'search',
      component: 'ChipGeneric',
    },

    advanced_filters: {
      serialize: v => encodeURIComponent(JSON.stringify(v)),
      component: 'ChipAdvancedFilter',
    },

    // date_range: {
    //   validate(v) {
    //     // [Date(), Date()]
    //     return (
    //       !v ||
    //       (v.length == 2 &&
    //         v[0] instanceof Date &&
    //         v[1] instanceof Date &&
    //         v[0] < v[1]) ||
    //       'Invalid date range'
    //     )
    //   },
    //   // Serialize: ["2022-07-20T14:22:34.911Z", "2022-08-29T14:22:34.911Z"]
    //   serialize: v => v.map(d => d.toISOString()),
    // },
    date_fields: {
      default: undefined,
      serialize: v => encodeURIComponent(v),
      component: 'ChipDateField',
    },

    date_range: {
      default: undefined,
      serialize: v => encodeURIComponent(v),
      component: 'ChipDateRange',
    },

    filter_dates: {
      default: undefined,
      serialize: v => encodeURIComponent(JSON.stringify(v)),
      component: 'ChipDateFilter',
    },

    directories: {
      // Directory ids: [1, 7, 28]
      serialize: v => encodeURIComponent(v),
      component: 'ChipGeneric',
    },

    sort_by: {
      default: 'current_workflow_limit',
      serialize: (v, state) => v + ':' + (state.sort_desc ? 'desc' : 'asc'),
      component: 'ChipGeneric',
    },

    sort_desc: {
      api_name: 'sort_order',
      default: false,
      serialize: v => (v ? 'D' : 'A'),
      component: 'ChipGeneric',
    },

    exclude_states: {
      api_name: 'exclude_states',
      default: false,
      component: 'ChipGeneric',
    },

    workflow_responsible: {
      api_name: 'current_workflow_responsible',
      component: 'ChipUser',
    },
  }

  constructor(filters) {
    filters = filters || {};

    let needs_verification = true;

    if (filters instanceof ExplorerFilters) {
      needs_verification = false;
      this._initial = deep_copy(filters._filters);
      this._filters = deep_copy(filters._filters);
    } else {
      this._initial = deep_copy(filters);
      this._filters = deep_copy(filters);
    }

    // Define handy getters and setters and set default properties
    for (const [key, props] of Object.entries(this.properties)) {
      Object.defineProperty(this, key, {
        get() {
          return this.get_value(key);
        },
        set(value) {
          return this.set_value(key, value);
        },
      });
      if (needs_verification)
        if (props.default !== undefined && this._filters[key] === undefined)
          this._filters[key] = props.default;
    }

    // Validate data
    if (needs_verification) {
      for (const [fkey, value] of Object.entries(this._filters)) {
        this.validate_or_throw(fkey, value);
      }
    }

    this._generate_instance_id();
  }

  validate_or_throw(fkey, value) {
    // Validate if value is valid for fkey or throw error
    if (this.properties[fkey] === undefined) {
      throw `Invalid property ${fkey}`;
    }

    if (this.properties[fkey].validate) {
      let ok = this.properties[fkey].validate(value);
      if (ok !== true) {
        throw `${fkey}: ${ok}`;
      }
    }

    return true;
  }

  merge(filters) {
    // Merge another ExplorerFilters to this
    if (!(filters instanceof ExplorerFilters))
      throw 'merge requires an ExplorerFilters instance';
    for (const [key, value] of Object.entries(filters._filters))
      if (value !== undefined) this._filters[key] = value;
    this._generate_instance_id();
    return this;
  }

  as_query() {
    // Return an object usable as a params query
    const res = {};
    let props = {};
    for (const [key, value] of Object.entries(this._filters)) {
      if (value !== undefined && value !== null) {
        props = this.properties[key];
        if (props.api_name !== null) {
          res[props.api_name || key] = props.serialize
            ? props.serialize(value, this._filters)
            : value;
        }
      }
    }
    return res;
  }

  get_value(fkey) {
    // Get current value for filter fkey
    return this._filters[fkey];
  }

  set_value(fkey, value) {
    // Set value to filter fkey
    if (!this.properties[fkey])
      throw `UNKNOWN ExplorerFilters PROPERTY ${fkey}`;
    this._filters[fkey] = value;
    this._generate_instance_id();
  }

  unset(property) {
    // Remove property from filters
    delete this._filters[property];
  }

  reset(property) {
    // Reset initial value
    if (this._initial[property] === undefined) {
      this.unset(property);
    } else {
      this._filters[property] = this._initial[property];
    }
  }

  reset_all() {
    // Reset all filters to their initail value
    this._filters = deep_copy(this._initial);
  }

  count() {
    // Count how many filter there are available
    return Object.values(this._filters).filter(v => v !== undefined).length;
  }

  _generate_instance_id() {
    // Generate a unique identifier
    this.instance_id = _explorer_filters_id++; // Vue reactive winks
  }
}

export default ExplorerFilters;
